// /app/frontend/src/pages/CommissionsPage.js
/**
 * Module de gestion des commissions commerciales.
 * - Configuration des commerciaux et leurs taux
 * - Dashboard des commissions par période
 * - Historique des paiements
 * - Export CSV/PDF
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Users,
  Euro,
  TrendingUp,
  Download,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Search,
  Calendar,
  CreditCard,
  BarChart3,
  FileText,
  Percent,
  Building2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CommissionsPage = () => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [periode, setPeriode] = useState('mois');
  
  // Commerciaux
  const [commerciaux, setCommerciaux] = useState([]);
  const [showCommercialDialog, setShowCommercialDialog] = useState(false);
  const [editingCommercial, setEditingCommercial] = useState(null);
  const [commercialForm, setCommercialForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    taux_vente_libre: 7,
    taux_privatisation: 10,
    notes: ''
  });
  
  // Commissions list
  const [commissions, setCommissions] = useState([]);
  const [commissionsTotal, setCommissionsTotal] = useState({});
  const [filterCommercial, setFilterCommercial] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  
  // Payments
  const [paiements, setPaiements] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    montant_paye: 0,
    mode_paiement: 'virement',
    reference_paiement: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/commissions/dashboard?periode=${periode}`, { headers });
      setDashboard(res.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [periode, token]);

  // Fetch commerciaux
  const fetchCommerciaux = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/commissions/commerciaux`, { headers });
      setCommerciaux(res.data);
    } catch (error) {
      console.error('Error fetching commerciaux:', error);
    }
  }, [token]);

  // Fetch commissions
  const fetchCommissions = useCallback(async () => {
    try {
      let url = `${API}/api/commissions/`;
      const params = new URLSearchParams();
      if (filterCommercial) params.append('commercial_id', filterCommercial);
      if (filterStatut) params.append('statut', filterStatut);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, { headers });
      setCommissions(res.data.commissions || []);
      setCommissionsTotal(res.data.totaux || {});
    } catch (error) {
      console.error('Error fetching commissions:', error);
    }
  }, [filterCommercial, filterStatut, token]);

  // Fetch payments
  const fetchPaiements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/commissions/paiements`, { headers });
      setPaiements(res.data.paiements || []);
    } catch (error) {
      console.error('Error fetching paiements:', error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchCommerciaux(),
        fetchCommissions(),
        fetchPaiements()
      ]);
      setLoading(false);
    };
    loadAll();
  }, []);

  // Reload on period change
  useEffect(() => {
    fetchDashboard();
  }, [periode, fetchDashboard]);

  // Reload commissions on filter change
  useEffect(() => {
    fetchCommissions();
  }, [filterCommercial, filterStatut, fetchCommissions]);

  // Save commercial
  const handleSaveCommercial = async () => {
    if (!commercialForm.nom) {
      toast.error('Le nom est requis');
      return;
    }
    
    try {
      setSubmitting(true);
      
      if (editingCommercial) {
        await axios.put(
          `${API}/api/commissions/commerciaux/${editingCommercial.id}`,
          commercialForm,
          { headers }
        );
        toast.success('Commercial mis à jour');
      } else {
        await axios.post(`${API}/api/commissions/commerciaux`, commercialForm, { headers });
        toast.success('Commercial créé');
      }
      
      setShowCommercialDialog(false);
      setEditingCommercial(null);
      resetCommercialForm();
      fetchCommerciaux();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete commercial
  const handleDeleteCommercial = async (id) => {
    if (!window.confirm('Supprimer ce commercial ?')) return;
    
    try {
      await axios.delete(`${API}/api/commissions/commerciaux/${id}`, { headers });
      toast.success('Commercial supprimé');
      fetchCommerciaux();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Record payment
  const handleRecordPayment = async () => {
    if (selectedCommissions.length === 0) {
      toast.error('Sélectionnez au moins une commission');
      return;
    }
    if (paymentForm.montant_paye <= 0) {
      toast.error('Montant invalide');
      return;
    }
    
    try {
      setSubmitting(true);
      
      await axios.post(`${API}/api/commissions/paiements`, {
        commission_ids: selectedCommissions,
        ...paymentForm
      }, { headers });
      
      toast.success('Paiement enregistré');
      setShowPaymentDialog(false);
      setSelectedCommissions([]);
      resetPaymentForm();
      fetchCommissions();
      fetchPaiements();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  // Export commissions
  const handleExport = async (format = 'csv') => {
    try {
      const res = await axios.get(`${API}/api/commissions/export?format=${format}`, { headers });
      
      if (format === 'csv' && res.data.csv_data) {
        const blob = new Blob([res.data.csv_data], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = res.data.filename || 'commissions_export.csv';
        link.click();
        toast.success('Export téléchargé');
      } else if (format === 'json') {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `commissions_export_${new Date().toISOString().slice(0, 10)}.json`;
        link.click();
        toast.success('Export téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const resetCommercialForm = () => {
    setCommercialForm({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      taux_vente_libre: 7,
      taux_privatisation: 10,
      notes: ''
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      montant_paye: 0,
      mode_paiement: 'virement',
      reference_paiement: '',
      notes: ''
    });
  };

  const openEditCommercial = (commercial) => {
    setEditingCommercial(commercial);
    setCommercialForm({
      nom: commercial.nom || '',
      prenom: commercial.prenom || '',
      email: commercial.email || '',
      telephone: commercial.telephone || '',
      taux_vente_libre: commercial.taux_vente_libre || 7,
      taux_privatisation: commercial.taux_privatisation || 10,
      notes: commercial.notes || ''
    });
    setShowCommercialDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'paye':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Payé</Badge>;
      case 'partiel':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Partiel</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700"><AlertCircle className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  // Calculate selected commissions total
  const selectedTotal = commissions
    .filter(c => selectedCommissions.includes(c.id))
    .reduce((sum, c) => sum + (c.montant_commission - (c.montant_paye || 0)), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#8B1538]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Percent className="h-6 w-6 text-[#8B1538]" />
            Gestion des Commissions
          </h1>
          <p className="text-gray-500 mt-1">
            Suivez les commissions des commerciaux et gérez les paiements
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            onClick={() => {
              resetCommercialForm();
              setEditingCommercial(null);
              setShowCommercialDialog(true);
            }}
            className="bg-[#8B1538] hover:bg-[#6B1028]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Commercial
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="commissions" data-testid="tab-commissions">
            <Euro className="w-4 h-4 mr-2" />
            Commissions
          </TabsTrigger>
          <TabsTrigger value="commerciaux" data-testid="tab-commerciaux">
            <Users className="w-4 h-4 mr-2" />
            Commerciaux
          </TabsTrigger>
          <TabsTrigger value="paiements" data-testid="tab-paiements">
            <CreditCard className="w-4 h-4 mr-2" />
            Paiements
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Period Selector */}
          <div className="flex items-center gap-4">
            <Label>Période :</Label>
            <Select value={periode} onValueChange={setPeriode}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mois">Ce mois</SelectItem>
                <SelectItem value="trimestre">Ce trimestre</SelectItem>
                <SelectItem value="annee">Cette année</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={fetchDashboard}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total CA HT</p>
                    <p className="text-2xl font-bold">{formatCurrency(dashboard?.totaux?.total_ca_ht)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Commissions</p>
                    <p className="text-2xl font-bold text-[#8B1538]">{formatCurrency(dashboard?.totaux?.total_commission)}</p>
                  </div>
                  <Euro className="h-8 w-8 text-[#8B1538]" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Payé</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboard?.totaux?.total_paye)}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Restant dû</p>
                    <p className="text-2xl font-bold text-amber-600">{formatCurrency(dashboard?.totaux?.total_restant)}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {dashboard?.totaux?.nb_en_attente || 0} commission(s) en attente
                </p>
              </CardContent>
            </Card>
          </div>

          {/* By Commercial */}
          {dashboard?.par_commercial?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Par Commercial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Commercial</TableHead>
                      <TableHead className="text-right">Vente Libre</TableHead>
                      <TableHead className="text-right">Privatisation</TableHead>
                      <TableHead className="text-right">Total CA</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead className="text-right">Payé</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.par_commercial.map((c) => (
                      <TableRow key={c.commercial_id}>
                        <TableCell className="font-medium">{c.commercial_nom}</TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm text-gray-500">{c.vente_libre.count} evt</span>
                          <br />
                          {formatCurrency(c.vente_libre.commission)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm text-gray-500">{c.privatisation.count} evt</span>
                          <br />
                          {formatCurrency(c.privatisation.commission)}
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(c.total_ca)}</TableCell>
                        <TableCell className="text-right font-semibold text-[#8B1538]">
                          {formatCurrency(c.total_commission)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(c.total_paye)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Top Events */}
          {dashboard?.par_evenement?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Top Événements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Événement</TableHead>
                      <TableHead className="text-right">Nb Commissions</TableHead>
                      <TableHead className="text-right">CA HT</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.par_evenement.map((e) => (
                      <TableRow key={e.event_id}>
                        <TableCell className="font-medium">{e.event_titre}</TableCell>
                        <TableCell className="text-right">{e.nb_commissions}</TableCell>
                        <TableCell className="text-right">{formatCurrency(e.total_ca)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(e.total_commission)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Commissions Tab */}
        <TabsContent value="commissions" className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label>Commercial:</Label>
              <Select value={filterCommercial} onValueChange={setFilterCommercial}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tous</SelectItem>
                  {commerciaux.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Label>Statut:</Label>
              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tous</SelectItem>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="partiel">Partiel</SelectItem>
                  <SelectItem value="paye">Payé</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedCommissions.length > 0 && (
              <Button 
                onClick={() => {
                  setPaymentForm(prev => ({ ...prev, montant_paye: selectedTotal }));
                  setShowPaymentDialog(true);
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Payer {selectedCommissions.length} commission(s) ({formatCurrency(selectedTotal)})
              </Button>
            )}
          </div>

          {/* Summary */}
          <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="text-sm text-gray-500">Total:</span>
              <span className="ml-2 font-semibold">{formatCurrency(commissionsTotal.total_montant)}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Payé:</span>
              <span className="ml-2 font-semibold text-green-600">{formatCurrency(commissionsTotal.total_paye)}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500">Restant:</span>
              <span className="ml-2 font-semibold text-amber-600">{formatCurrency(commissionsTotal.total_restant)}</span>
            </div>
          </div>

          {/* Commissions Table */}
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedCommissions.length === commissions.filter(c => c.statut !== 'paye').length && commissions.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedCommissions(commissions.filter(c => c.statut !== 'paye').map(c => c.id));
                          } else {
                            setSelectedCommissions([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Commercial</TableHead>
                    <TableHead>Événement</TableHead>
                    <TableHead>Food Truck</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">CA HT</TableHead>
                    <TableHead className="text-right">Taux</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        {c.statut !== 'paye' && (
                          <Checkbox
                            checked={selectedCommissions.includes(c.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCommissions([...selectedCommissions, c.id]);
                              } else {
                                setSelectedCommissions(selectedCommissions.filter(id => id !== c.id));
                              }
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(c.created_at)}</TableCell>
                      <TableCell>{c.commercial_nom}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{c.event_titre}</TableCell>
                      <TableCell>{c.food_truck_nom}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {c.type_prestation === 'privatisation' ? 'Privatisation' : 'Vente libre'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(c.ca_ht)}</TableCell>
                      <TableCell className="text-right">{c.taux_applique}%</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(c.montant_commission)}</TableCell>
                      <TableCell>{getStatutBadge(c.statut)}</TableCell>
                    </TableRow>
                  ))}
                  {commissions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                        Aucune commission trouvée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Commerciaux Tab */}
        <TabsContent value="commerciaux" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {commerciaux.map((c) => (
              <Card key={c.id} className={!c.actif ? 'opacity-60' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{c.prenom} {c.nom}</CardTitle>
                      {c.email && <CardDescription>{c.email}</CardDescription>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditCommercial(c)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCommercial(c.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {c.telephone && (
                      <p className="text-sm text-gray-600">{c.telephone}</p>
                    )}
                    <div className="flex items-center gap-4 pt-2 border-t">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Vente libre</p>
                        <p className="text-lg font-bold text-blue-600">{c.taux_vente_libre}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Privatisation</p>
                        <p className="text-lg font-bold text-purple-600">{c.taux_privatisation}%</p>
                      </div>
                    </div>
                    {!c.actif && (
                      <Badge variant="secondary" className="mt-2">Inactif</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {commerciaux.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun commercial configuré</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => {
                      resetCommercialForm();
                      setShowCommercialDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un commercial
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Paiements Tab */}
        <TabsContent value="paiements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Historique des paiements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Commercial</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Référence</TableHead>
                    <TableHead>Par</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paiements.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{formatDate(p.created_at)}</TableCell>
                      <TableCell>{p.commercial_nom}</TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        {formatCurrency(p.montant_paye)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{p.mode_paiement}</Badge>
                      </TableCell>
                      <TableCell>{p.reference_paiement || '-'}</TableCell>
                      <TableCell className="text-sm text-gray-500">{p.created_by_name}</TableCell>
                    </TableRow>
                  ))}
                  {paiements.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        Aucun paiement enregistré
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Commercial Dialog */}
      <Dialog open={showCommercialDialog} onOpenChange={setShowCommercialDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCommercial ? 'Modifier le commercial' : 'Nouveau commercial'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom</Label>
                <Input
                  value={commercialForm.prenom}
                  onChange={(e) => setCommercialForm({ ...commercialForm, prenom: e.target.value })}
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={commercialForm.nom}
                  onChange={(e) => setCommercialForm({ ...commercialForm, nom: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={commercialForm.email}
                onChange={(e) => setCommercialForm({ ...commercialForm, email: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Téléphone</Label>
              <Input
                value={commercialForm.telephone}
                onChange={(e) => setCommercialForm({ ...commercialForm, telephone: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Taux Vente Libre (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={commercialForm.taux_vente_libre}
                  onChange={(e) => setCommercialForm({ ...commercialForm, taux_vente_libre: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Taux Privatisation (%)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={commercialForm.taux_privatisation}
                  onChange={(e) => setCommercialForm({ ...commercialForm, taux_privatisation: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={commercialForm.notes}
                onChange={(e) => setCommercialForm({ ...commercialForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommercialDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveCommercial}
              disabled={submitting}
              className="bg-[#8B1538] hover:bg-[#6B1028]"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingCommercial ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                {selectedCommissions.length} commission(s) sélectionnée(s)
              </p>
              <p className="text-lg font-bold text-blue-900">
                Total à payer : {formatCurrency(selectedTotal)}
              </p>
            </div>
            
            <div>
              <Label>Montant payé *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={selectedTotal}
                value={paymentForm.montant_paye}
                onChange={(e) => setPaymentForm({ ...paymentForm, montant_paye: parseFloat(e.target.value) || 0 })}
              />
            </div>
            
            <div>
              <Label>Mode de paiement</Label>
              <Select 
                value={paymentForm.mode_paiement} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, mode_paiement: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Référence (optionnel)</Label>
              <Input
                value={paymentForm.reference_paiement}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference_paiement: e.target.value })}
                placeholder="N° de virement, chèque..."
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleRecordPayment}
              disabled={submitting || paymentForm.montant_paye <= 0}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionsPage;
